import axios from 'axios';

// Types for API requests and responses
export interface User {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  profilePicture?: string;
  bio?: string;
  location?: string;
  joinDate: string;
  isVerified: boolean;
  rating: number;
  totalSales: number;
  totalPurchases: number;
  preferences: {
    notifications: boolean;
    darkMode: boolean;
    language: string;
  };
}

export interface Post {
  postId: string;
  userId: string;
  title: string;
  description: string;
  price: number;
  originalPrice?: number;
  condition: 'new' | 'like-new' | 'good' | 'fair' | 'poor';
  categoryId: string;
  brandId?: string;
  images: string[];
  status: 'active' | 'sold' | 'pending' | 'inactive';
  location?: string;
  tags: string[];
  viewCount: number;
  favoriteCount: number;
  negotiable: boolean;
  shippingAvailable: boolean;
  pickupAvailable: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePostRequest {
  title: string;
  description: string;
  price: number;
  originalPrice?: number;
  condition: Post['condition'];
  categoryId: string;
  brandId?: string;
  images: string[];
  location?: string;
  tags: string[];
  negotiable: boolean;
  shippingAvailable: boolean;
  pickupAvailable: boolean;
}

export interface Message {
  messageId: string;
  conversationId: string;
  senderId: string;
  receiverId: string;
  postId?: string;
  content: string;
  messageType: 'text' | 'image' | 'offer' | 'system';
  isRead: boolean;
  createdAt: string;
}

export interface Conversation {
  conversationId: string;
  participants: string[];
  postId?: string;
  lastMessageAt: string;
  lastMessage: string;
  unreadCount: number;
  isArchived: boolean;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    message: string;
    code?: string;
    details?: any;
  };
}

export interface PaginatedResponse<T> {
  items: T[];
  nextToken?: string;
  totalCount?: number;
}

// Configure axios defaults
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://h7es6qyuyh.execute-api.eu-west-2.amazonaws.com/dev';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized - redirect to login
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Authentication API
export const authApi = {
  login: async (credentials: LoginRequest): Promise<{ 
    user: User; 
    accessToken: string; 
    refreshToken: string; 
    idToken: string; 
    expiresIn: number; 
  }> => {
    const response = await apiClient.post<ApiResponse<{ 
      user: User; 
      accessToken: string; 
      refreshToken: string; 
      idToken: string; 
      expiresIn: number; 
    }>>('/auth/login', credentials);
    return response.data.data!;
  },

  register: async (userData: RegisterRequest): Promise<{ user: User; message: string }> => {
    const response = await apiClient.post<ApiResponse<{ user: User; message: string }>>('/auth/register', userData);
    return response.data.data!;
  },

  refreshToken: async (refreshToken: string): Promise<{ 
    accessToken: string; 
    idToken: string; 
    expiresIn: number; 
  }> => {
    const response = await apiClient.post<ApiResponse<{ 
      accessToken: string; 
      idToken: string; 
      expiresIn: number; 
    }>>('/auth/refresh', { refreshToken });
    return response.data.data!;
  },
};

// User API
export const userApi = {
  getProfile: async (userId: string): Promise<User> => {
    const response = await apiClient.get<ApiResponse<User>>(`/users/${userId}`);
    return response.data.data!;
  },

  updateProfile: async (userId: string, updates: Partial<User>): Promise<User> => {
    const response = await apiClient.put<ApiResponse<User>>(`/users/${userId}`, updates);
    return response.data.data!;
  },

  getUserPosts: async (userId: string, limit = 20, nextToken?: string): Promise<PaginatedResponse<Post>> => {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    if (nextToken) params.append('nextToken', nextToken);
    
    const response = await apiClient.get<ApiResponse<PaginatedResponse<Post>>>(
      `/users/${userId}/posts?${params.toString()}`
    );
    return response.data.data!;
  },
};

// Posts API
export const postsApi = {
  getPosts: async (filters?: {
    category?: string;
    minPrice?: number;
    maxPrice?: number;
    condition?: string;
    location?: string;
    search?: string;
    sortBy?: 'date' | 'price' | 'views';
    sortOrder?: 'asc' | 'desc';
    limit?: number;
    nextToken?: string;
  }): Promise<PaginatedResponse<Post>> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    
    const response = await apiClient.get<ApiResponse<PaginatedResponse<Post>>>(
      `/posts${params.toString() ? `?${params.toString()}` : ''}`
    );
    return response.data.data!;
  },

  getPost: async (postId: string): Promise<Post> => {
    const response = await apiClient.get<ApiResponse<Post>>(`/posts/${postId}`);
    return response.data.data!;
  },

  createPost: async (postData: CreatePostRequest): Promise<Post> => {
    const response = await apiClient.post<ApiResponse<Post>>('/posts', postData);
    return response.data.data!;
  },

  updatePost: async (postId: string, updates: Partial<CreatePostRequest>): Promise<Post> => {
    const response = await apiClient.put<ApiResponse<Post>>(`/posts/${postId}`, updates);
    return response.data.data!;
  },

  deletePost: async (postId: string): Promise<void> => {
    await apiClient.delete(`/posts/${postId}`);
  },

  getUserPosts: async (userId: string, limit = 20, nextToken?: string): Promise<PaginatedResponse<Post>> => {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    if (nextToken) params.append('nextToken', nextToken);
    
    const response = await apiClient.get<ApiResponse<PaginatedResponse<Post>>>(
      `/users/${userId}/posts?${params.toString()}`
    );
    return response.data.data!;
  },
};

// Messaging API
export const messagingApi = {
  getConversations: async (limit = 20, nextToken?: string): Promise<PaginatedResponse<Conversation>> => {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    if (nextToken) params.append('nextToken', nextToken);
    
    const response = await apiClient.get<ApiResponse<PaginatedResponse<Conversation>>>(
      `/messages/conversations?${params.toString()}`
    );
    return response.data.data!;
  },

  getMessages: async (conversationId: string, limit = 50, nextToken?: string): Promise<PaginatedResponse<Message>> => {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    if (nextToken) params.append('nextToken', nextToken);
    
    const response = await apiClient.get<ApiResponse<PaginatedResponse<Message>>>(
      `/messages/${conversationId}?${params.toString()}`
    );
    return response.data.data!;
  },

  sendMessage: async (messageData: {
    conversationId?: string;
    receiverId: string;
    postId?: string;
    content: string;
    messageType: Message['messageType'];
    offerAmount?: number;
  }): Promise<Message> => {
    const response = await apiClient.post<ApiResponse<Message>>('/messages', messageData);
    return response.data.data!;
  },
};

// File Upload API
export const uploadApi = {
  getUploadUrl: async (fileData: {
    fileName: string;
    fileType: string;
    fileSize: number;
  }): Promise<{
    uploadUrl: string;
    fileUrl: string;
    fields?: { [key: string]: string };
  }> => {
    const response = await apiClient.post<ApiResponse<{
      uploadUrl: string;
      fileUrl: string;
      fields?: { [key: string]: string };
    }>>('/upload/url', fileData);
    return response.data.data!;
  },

  uploadFile: async (file: File, uploadData: {
    uploadUrl: string;
    fields?: { [key: string]: string };
  }): Promise<void> => {
    const formData = new FormData();
    
    // Add any additional fields first
    if (uploadData.fields) {
      Object.entries(uploadData.fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }
    
    // Add the file last
    formData.append('file', file);
    
    // Upload directly to S3
    await fetch(uploadData.uploadUrl, {
      method: 'POST',
      body: formData,
    });
  },
};

// Utility function to check if we're in development mode
export const isDevelopment = import.meta.env.DEV;

// Categories API
export const categoriesApi = {
  getCategories: async (): Promise<{ categories: Category[] }> => {
    const response = await apiClient.get<ApiResponse<{ categories: Category[] }>>('/categories');
    return response.data.data!;
  },
};

// Add Category interface if not exists
export interface Category {
  id: string;
  name: string;
  description: string;
}

// Fallback to mock data in development if API is not available
export const shouldUseMockData = isDevelopment && !import.meta.env.VITE_API_BASE_URL; 